'use client';

import { castArray } from 'lodash-es';
import {
    createContext,
    Key,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { BreakpointValue, useBreakpointValue } from '@/hooks/useBreakpoint';

type IntersectionItemState = {
    ratio: number;
};

type IntersectionState = {
    state: Map<Key, IntersectionItemState>;
};

type IntersectionActions = {
    observe: (id: Key, element: HTMLElement) => () => void;
};

const IntersectionStateContext = createContext<IntersectionState | null>(null);

const IntersectionActionContext = createContext<IntersectionActions | null>(
    null
);

export function useIntersectionState() {
    const context = useContext(IntersectionStateContext);

    if (!context) {
        throw new Error(
            'useIntersectionState must be used within an IntersectionProvider'
        );
    }

    return context;
}

export function useObserve() {
    const context = useContext(IntersectionActionContext);

    if (!context) {
        throw new Error(
            'useObserve must be used within an IntersectionProvider'
        );
    }

    return context;
}

export function useIsIntersecting(id: string | string[]): boolean {
    const context = useContext(IntersectionStateContext);

    if (!context) {
        throw new Error(
            'useIsIntersecting must be used within an IntersectionProvider'
        );
    }

    const ids = castArray(id);

    return ids.every((id) => context.state.has(id));
}

type IntersectionProviderProps = {
    children: ReactNode;
    root?: RefObject<Element | null>;
    rootMargin?: BreakpointValue<string>;
};

export function IntersectionProvider({
    children,
    root,
    rootMargin,
}: IntersectionProviderProps) {
    const observer = useRef<IntersectionObserver | null>(null);
    const map = useRef<Map<Element, Key>>(new Map());
    const [state, setState] = useState<Map<Key, IntersectionItemState>>(
        new Map()
    );

    const rootMarginValue = useBreakpointValue(rootMargin);

    const markIntersecting = useCallback(
        (id: Key, state: IntersectionItemState) => {
            setState((prev) => new Map(prev).set(id, state));
        },
        []
    );

    const clearIntersecting = useCallback((id: Key) => {
        setState((prev) => {
            if (!prev.has(id)) {
                return prev;
            }

            const nextState = new Map(prev);
            nextState.delete(id);

            return nextState;
        });
    }, []);

    const observe = useCallback(
        (id: Key, element: HTMLElement) => {
            observer.current?.observe(element);
            map.current.set(element, id);

            return () => {
                observer.current?.unobserve(element);
                map.current.delete(element);
                clearIntersecting(id);
            };
        },
        [clearIntersecting]
    );

    useEffect(() => {
        observer.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const id = map.current.get(entry.target);

                    if (!id) {
                        return;
                    }

                    if (entry.isIntersecting) {
                        markIntersecting(id, {
                            ratio: entry.intersectionRatio,
                        });
                    } else {
                        clearIntersecting(id);
                    }
                });
            },
            {
                root: root?.current,
                rootMargin: rootMarginValue,
                threshold: [0, 1],
            }
        );

        for (const element of map.current.keys()) {
            observer.current.observe(element);
        }

        return () => {
            observer.current?.disconnect();
        };
    }, [root, rootMarginValue, clearIntersecting, markIntersecting, observe]);

    const actionContext = useMemo<IntersectionActions>(
        () => ({
            observe,
        }),
        [observe]
    );

    const stateContext = useMemo<IntersectionState>(() => ({ state }), [state]);

    return (
        <IntersectionActionContext.Provider value={actionContext}>
            <IntersectionStateContext.Provider value={stateContext}>
                {children}
            </IntersectionStateContext.Provider>
        </IntersectionActionContext.Provider>
    );
}
